/**
 * Person detail — "People Management" 8a, screen 3.
 * Route: /team/:id
 * @file src/web/features/team/pages/[id].tsx
 *
 * @llm-rule WHEN: A Company Admin or Head opens one person from the People list
 * @llm-rule NOTE: Everything on this screen changes someone else's access —
 *   who they report to, the face their account matches, their password, whether
 *   they can sign in at all. Each one confirms first and says what it does in
 *   plain words, because none of them are undoable by the person affected.
 * @llm-rule AVOID: Offering these to a manager. The roster is set by the Company Admin
 *   or a Head; the route guards itself rather than trusting a hidden nav link.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
  useConfirm,
} from '@bloomneo/uikit';
import { ArrowLeft, KeyRound, ScanFace, UserCog } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { Panel, PersonAvatar } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { ROLE_LABEL, type OrgRole } from '../../../shared/domain';

interface Person {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  orgRole: OrgRole;
  title: string | null;
  initials: string | null;
  managerId: string | null;
  isActive: boolean;
  faceEnrolledAt?: string | null;
}

interface FaceIdentity {
  enrolledAt: string | null;
  image: string | null;
  canReset: boolean;
}

function when(iso: string | null): string {
  if (!iso) return 'not yet';
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/* -------------------------------------------------------------------------- */
/* An account row — dashed frame, icon, fact, and one action.                 */
/* -------------------------------------------------------------------------- */

function AccountRow({
  icon,
  title,
  detail,
  actionLabel,
  onAction,
  busy,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  actionLabel: string;
  onAction: () => void;
  busy?: boolean;
}) {
  return (
    <div
      className="flex items-center gap-3 rounded-[12px] px-3.5 py-3"
      style={{ border: '2px dashed var(--rule)' }}
    >
      <span className="grid size-10 flex-none place-items-center rounded-[9px] border-2 border-ink text-ink">
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-medium text-ink">{title}</div>
        <div className="text-[12px] text-ink-faint">{detail}</div>
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={onAction}
        className="flex-none text-[13px] font-medium underline underline-offset-4 disabled:opacity-50"
        style={{ color: 'var(--dot-flag)' }}
      >
        {actionLabel}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function PersonPage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const { request } = useAppRequest();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [person, setPerson] = useState<Person | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [face, setFace] = useState<FaceIdentity | null>(null);
  /** The enrolment photo, opened full size. A 36px thumbnail is too small to
   *  actually recognise someone from, which is the whole point of showing it. */
  const [faceOpen, setFaceOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveTo, setMoveTo] = useState('');

  const role = (user?.orgRole as OrgRole) ?? 'EMPLOYEE';
  const isCeoOrCto = role === 'COMPANY_ADMIN' || role === 'HEAD';

  const load = useCallback(async () => {
    const [roster, faceResult] = await Promise.all([
      request<{ people: Person[] }>('/api/org/people'),
      request<FaceIdentity>(`/api/attendance/face/${id}`),
    ]);

    if (roster.ok) {
      setPeople(roster.data.people);
      setPerson(roster.data.people.find((p) => p.id === id) ?? null);
    } else {
      toast.error(roster.error);
    }

    if (faceResult.ok) setFace(faceResult.data);
    setLoading(false);
  }, [request, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const manager = people.find((p) => p.id === person?.managerId) ?? null;
  // Only someone who could actually supervise them.
  const candidates = people.filter(
    (p) => p.id !== id && (p.orgRole === 'MANAGER' || p.orgRole === 'HEAD' || p.orgRole === 'COMPANY_ADMIN'),
  );

  async function saveManager() {
    setBusy(true);
    const result = await request(`/api/org/people/${id}`, {
      method: 'PUT',
      body: { managerId: moveTo || null },
    });
    setBusy(false);
    if (!result.ok) return toast.error(result.error);

    toast.success(
      moveTo
        ? `${person?.name} now reports to ${people.find((p) => p.id === moveTo)?.name}`
        : `${person?.name} no longer reports to anyone`,
    );
    setMoving(false);
    await load();
  }

  async function resetFace() {
    const ok = await confirm({
      title: `Reset ${person?.name}’s face?`,
      description:
        'The photo on file is deleted and they enrol again at their next clock-in. Whoever enrols next becomes the face this account accepts.',
      confirmLabel: 'Reset enrolment',
    });
    if (!ok) return;

    setBusy(true);
    const result = await request<{ message: string }>(`/api/attendance/face/${id}/reset`, {
      method: 'POST',
    });
    setBusy(false);
    if (!result.ok) return toast.error(result.error);
    toast.success(result.data.message);
    await load();
  }

  async function resetPassword() {
    const ok = await confirm({
      title: `Issue a new password for ${person?.name}?`,
      description: 'Their current password stops working immediately. The new one is shown once.',
      confirmLabel: 'Issue new password',
    });
    if (!ok) return;

    setBusy(true);
    const result = await request<{ temporaryPassword: string }>(
      `/api/org/people/${id}/reset-password`,
      { method: 'POST' },
    );
    setBusy(false);
    if (!result.ok) return toast.error(result.error);

    // Shown once — a toast that disappears would lose it.
    await confirm({
      title: 'New password',
      description: `${person?.name}: ${result.data.temporaryPassword}\n\nHand this over directly. It is not shown again.`,
      confirmLabel: 'Copied it',
    });
  }

  /** Permanent removal. Two-step on purpose: the confirm spells out the loss. */
  async function removePerson() {
    if (!person) return;
    const ok = await confirm({
      title: `Delete ${person.name} permanently?`,
      description:
        'This removes the account and everything keyed to it — attendance history, flags, ' +
        'tasks, leave and payslips. It cannot be undone. To keep the record, deactivate instead.',
      confirmLabel: 'Delete permanently',
      destructive: true,
    } as never);
    if (!ok) return;

    setBusy(true);
    try {
      const result = await request(`/api/org/people/${person.id}`, { method: 'DELETE' });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      toast.success(`${person.name} deleted`);
      navigate('/team');
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive() {
    if (!person) return;
    const deactivating = person.isActive;

    const ok = await confirm({
      title: deactivating ? `Deactivate ${person.name}?` : `Reactivate ${person.name}?`,
      description: deactivating
        ? 'They can no longer sign in. Their attendance, flags and tasks are kept.'
        : 'They can sign in again with their existing password.',
      confirmLabel: deactivating ? 'Deactivate' : 'Reactivate',
    });
    if (!ok) return;

    setBusy(true);
    const result = await request(`/api/org/people/${id}`, {
      method: 'PUT',
      body: { isActive: !person.isActive },
    });
    setBusy(false);
    if (!result.ok) return toast.error(result.error);

    toast.success(deactivating ? 'Account deactivated' : 'Account reactivated');
    await load();
  }

  if (!isCeoOrCto) return <Navigate to="/clock" replace />;

  return (
    <AppShell>
      <Button variant="ghost" size="sm" className="mb-3 -ml-2" onClick={() => navigate('/team')}>
        <ArrowLeft className="mr-1.5 size-4" />
        People
      </Button>

      {loading ? (
        <Skeleton className="h-96 w-full rounded-[18px]" />
      ) : !person ? (
        <Panel>
          <p className="text-[13.5px] text-ink-soft">That person could not be loaded.</p>
        </Panel>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Identity */}
          <div
            className="flex items-center gap-3 rounded-[13px] bg-paper p-3.5"
            style={{ border: '2px solid var(--ink)' }}
          >
            <PersonAvatar person={person} size={44} />
            <div className="min-w-0">
              <h1 className="truncate text-[17px] font-semibold text-ink">{person.name}</h1>
              <p className="truncate text-[12.5px] text-ink-faint">
                {person.title || ROLE_LABEL[person.orgRole]} · {person.email}
              </p>
            </div>
            {!person.isActive && (
              <span
                className="label-mono ml-auto flex-none rounded-md px-2 py-0.5 text-[10px]"
                style={{ background: 'var(--state-bad-bg)', color: 'var(--dot-flag)' }}
              >
                Inactive
              </span>
            )}
          </div>

          {/* Reporting line */}
          <div>
            <div className="label-mono mb-2 text-ink-faint">Reporting line</div>

            {moving ? (
              <div
                className="flex flex-col gap-2.5 rounded-[12px] bg-paper p-3.5"
                style={{ border: '2px solid var(--ink)' }}
              >
                <Select value={moveTo} onValueChange={setMoveTo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick who they report to" />
                  </SelectTrigger>
                  <SelectContent>
                    {candidates.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} · {ROLE_LABEL[p.orgRole]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex gap-2">
                  <Button className="btn-brand flex-1" disabled={busy} onClick={() => void saveManager()}>
                    Save
                  </Button>
                  <Button variant="outline" onClick={() => setMoving(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="flex items-center gap-2.5 rounded-[12px] bg-paper px-3.5 py-3"
                style={{ border: '2px solid var(--ink)' }}
              >
                <UserCog className="size-4 flex-none text-ink-faint" strokeWidth={1.8} />
                <span className="min-w-0 flex-1 truncate text-[14px] text-ink">
                  {manager ? `Reports to ${manager.name}` : 'Reports to nobody yet'}
                  {manager && (
                    <span className="text-ink-faint"> · {ROLE_LABEL[manager.orgRole]}</span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setMoveTo(person.managerId ?? '');
                    setMoving(true);
                  }}
                  className="flex-none text-[13px] font-medium underline underline-offset-4"
                  style={{ color: 'var(--dot-flag)' }}
                >
                  Move
                </button>
              </div>
            )}

            <p className="mt-1.5 text-[11.5px] text-ink-ghost">
              Sets where their attendance, tasks and flags show up.
            </p>
          </div>

          {/* Account */}
          <div>
            <div className="label-mono mb-2 text-ink-faint">Account</div>
            <div className="flex flex-col gap-2">
              {/*
                The enrolment photo, shown rather than described.
                "Enrolled 14 Aug" tells a Head that SOMETHING is on file; the
                picture is what lets them confirm it is the right person. It is
                the only image kept — clock-ins are matched against it and store
                nothing of their own.
              */}
              <AccountRow
                icon={
                  face?.image ? (
                    <button
                      type="button"
                      onClick={() => setFaceOpen(true)}
                      aria-label={`See ${person.name ?? 'this person'}'s enrolment photo full size`}
                      className="rounded-[8px] transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink"
                    >
                      <img
                        src={face.image}
                        alt={`${person.name ?? 'This person'} at enrolment`}
                        className="size-9 rounded-[8px] object-cover"
                      />
                    </button>
                  ) : (
                    <ScanFace className="size-5" strokeWidth={1.8} />
                  )
                }
                title="Face ID"
                detail={
                  face?.enrolledAt
                    ? `Enrolled ${when(face.enrolledAt)}${face.image ? '' : ' · no photo on file'}`
                    : 'Not enrolled yet'
                }
                actionLabel="Reset"
                busy={busy || !face?.enrolledAt}
                onAction={() => void resetFace()}
              />

              <AccountRow
                icon={<KeyRound className="size-5" strokeWidth={1.8} />}
                title="Password"
                detail="Issue a new one if they are locked out"
                actionLabel="Reset"
                busy={busy}
                onAction={() => void resetPassword()}
              />
            </div>
          </div>

          {/* The heavy one, last. */}
          <div className="mt-2 flex flex-col gap-2">
            <Button
              variant="outline"
              className="h-12 rounded-[13px]"
              disabled={busy}
              onClick={() => void toggleActive()}
              style={person.isActive ? { color: 'var(--dot-flag)' } : undefined}
            >
              {person.isActive ? 'Deactivate account' : 'Reactivate account'}
            </Button>

            {/* Deletion is destructive and irreversible, so it sits below
                deactivation and is worded to say what it actually does. */}
            {(role === 'COMPANY_ADMIN' || role === 'HEAD') && person.id !== user?.id && (
              <Button
                variant="outline"
                className="h-12 rounded-[13px]"
                disabled={busy}
                onClick={() => void removePerson()}
                style={{ color: 'var(--dot-flag)' }}
              >
                Delete permanently
              </Button>
            )}

            <p className="text-center text-[11.5px] text-ink-ghost">
              Resets and reassignments are recorded against whoever made them. Deleting also
              removes their attendance, flags, tasks, leave and payslips — deactivate instead if
              you may need the record.
            </p>

            <Link
              to={`/oversight/${person.id}`}
              className="mt-1 text-center text-[12.5px] font-medium text-brand hover:underline"
            >
              See their flags and tasks →
            </Link>
          </div>
        </div>
      )}
      {/* The enrolment photo, full size.
          A Head confirming identity needs to actually see the face; the row's
          36px thumbnail only says that a photo exists. */}
      {face?.image && person && (
        <Dialog open={faceOpen} onOpenChange={setFaceOpen}>
          <DialogContent className="w-[calc(100vw-1.5rem)] max-w-sm sm:w-full">
            <DialogHeader>
              <DialogTitle>{person.name ?? 'Enrolment photo'}</DialogTitle>
            </DialogHeader>

            <img
              src={face.image}
              alt={`${person.name ?? 'This person'} at enrolment`}
              className="w-full rounded-[12px] object-contain"
            />

            <p className="text-[11.5px] leading-relaxed text-ink-soft">
              Taken once, at enrolment. Every clock-in is matched against this photo.
              {face.enrolledAt ? ` Enrolled ${when(face.enrolledAt)}.` : ''}
            </p>
          </DialogContent>
        </Dialog>
      )}

    </AppShell>
  );
}

export default function PersonRoute() {
  return (
    <AuthGuard>
      <PersonPage />
    </AuthGuard>
  );
}
